import { trans } from "@mongez/localization";
import { CustomOptionResponse } from "core/cart";
import { cn } from "core/shared";
import { formatDate } from "date-fns";
import { useState } from "react";
import { FancyBox } from "~/modules/shared/components/FancyBox";

function ReadMoreText({
  text,
  maxLength = 50,
}: {
  text: string;
  maxLength?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  if (text.length <= maxLength) {
    return <span className="truncate text-wrap text-gray-700">{text}</span>;
  }

  return (
    <span className="text-wrap text-gray-700">
      {isExpanded ? text : `${text.slice(0, maxLength)}...`}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-primary-default ml-1 cursor-pointer text-[10px] font-bold underline"
      >
        {isExpanded ? trans("readLess") : trans("readMore")}
      </button>
    </span>
  );
}

type CustomOptionsDisplayProps = {
  customOptions?: CustomOptionResponse[];
  classes?: {
    root?: string;
    option?: string;
  };
};

export function CustomOptionsDisplay({
  customOptions,
  classes,
}: CustomOptionsDisplayProps) {
  if (!customOptions || customOptions.length === 0) return null;

  return (
    <div className={cn("mt-2 flex flex-wrap gap-2", classes?.root)}>
      {customOptions.map((option) => {
        if (option.option?.type === "image" || option.option?.type === "file") {
          return (
            <div
              key={option.optionId}
              className={cn(
                "flex items-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium",
                classes?.option,
              )}
            >
              <span className="text-night mr-1 truncate font-semibold">
                {option.option?.name}:
              </span>
              <div className="flex items-center gap-1">
                {(option.value as any[])?.map((item, index) => (
                  <FancyBox
                    options={{
                      Hash: false,
                    }}
                    key={index}
                  >
                    <a
                      data-fancybox
                      href={item.url as string}
                      className="block"
                    >
                      <img
                        src={item.url as string}
                        alt={option.option?.name}
                        className="h-8 w-8 object-cover"
                      />
                    </a>
                  </FancyBox>
                ))}
              </div>
            </div>
          );
        }

        if (option.option?.type === "date") {
          return (
            <div
              key={option.optionId}
              className={cn(
                "flex items-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium",
                classes?.option,
              )}
            >
              <span className="text-night mr-1 truncate font-semibold">
                {option.option?.name}:
              </span>
              <span className="truncate text-wrap text-gray-700">
                {formatDate(option.value as string, "dd/MM/yyyy")}
              </span>
            </div>
          );
        }

        return (
          <div
            key={option.optionId}
            className={cn(
              "flex flex-wrap items-center rounded-md border border-gray-300 px-2 py-1 text-xs font-medium",
              classes?.option,
            )}
          >
            <span className="text-night mr-1 min-w-fit truncate font-semibold">
              {option.option?.name}:
            </span>
            {typeof option.value === "object" ? (
              Array.isArray(option.value) ? (
                <span className="truncate text-wrap text-gray-700">
                  {option.value.map((item) => item.name).join(", ")}
                </span>
              ) : (
                <span className="truncate text-wrap text-gray-700">
                  <ReadMoreText text={String(option.value.name)} maxLength={30} />
                </span>
              )
            ) : (
              <ReadMoreText text={String(option.value)} maxLength={30} />
            )}
          </div>
        );
      })}
    </div>
  );
}
